import { Link } from 'react-router';
import classes from 'classnames';
import React, { memo, ReactNode, useCallback } from 'react';
import Loader from '@/components/Button/Loader/Loader';
import ExternalIcon from '@/assets/icons/external.svg?react';
import styles from './Button.module.scss';
import useEventCollector from '@/hooks/context/EventCollector/useEventCollector';
import ButtonEvent from '@/hooks/context/EventCollector/events/ButtonEvent';
import { EventCollectorProps } from '@/hooks/context/EventCollector/types';

export type Props = {
  type?: 'submit' | 'button';
  children: ReactNode;
  outline?: boolean;
  disabled?: boolean;
  className?: string;
  dataTest?: string;
  disableStyles?: boolean;
  wide?: boolean;
  pending?: boolean;
  size?: 's' | 'm' | 'l';
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onMouseEnter?: (
    event?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>,
  ) => void;
  /* When given, makes button act as link */
  to?: string;
  /* Used when `to` is passed. Determines whether to use inner router or link to external site */
  external?: boolean;
  buttonClassName?: string;
};

const ButtonMemo = memo(function Button({
  children,
  type = 'button',
  outline = false,
  disabled = false,
  wide = false,
  pending = false,
  className,
  disableStyles = false,
  dataTest = 'button',
  size = 'l',
  onClick,
  onMouseEnter,
  to = '',
  external,
  buttonClassName,
  eventCollector,
}: Props & EventCollectorProps) {
  const { track } = useEventCollector();
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);

      if (eventCollector?.dontTrack) {
        return;
      }

      track(
        new ButtonEvent({
          description:
            eventCollector?.description ?? children?.toString() ?? '',
          content: children?.toString() ?? '',
        }),
      );
    },
    [
      eventCollector?.description,
      eventCollector?.dontTrack,
      children,
      onClick,
      track,
    ],
  );

  const buttonStyle = !disableStyles
    ? classes(
        styles.button,
        outline && styles.outline,
        pending && styles.pending,
        styles[size],
        wide && styles.wide,
        buttonClassName,
      )
    : buttonClassName;

  return (
    <div
      className={
        !disableStyles
          ? classes(
              styles.container,
              styles[size],
              wide && styles.wide,
              className,
            )
          : className
      }
    >
      {to && !external && (
        <Link
          className={buttonStyle}
          onMouseEnter={onMouseEnter}
          data-test={dataTest}
          to={to}
        >
          {!(size === 's' && pending) && children}
          {pending && (
            <div className={styles[`loader-wrapper-${size}`]}>
              <Loader outline={outline} />
            </div>
          )}
        </Link>
      )}
      {to && external && (
        <a
          className={buttonStyle}
          onMouseEnter={onMouseEnter}
          data-test={dataTest}
          href={to}
          rel="noopener"
        >
          {!(size === 's' && pending) && children}
          {external && <ExternalIcon className={styles['external-icon']} />}
          {pending && (
            <div className={styles[`loader-wrapper`]}>
              <Loader outline={outline} />
            </div>
          )}
        </a>
      )}
      {!to && (
        <button
          data-test={dataTest}
          disabled={disabled || pending}
          type={type === 'submit' ? 'submit' : 'button'}
          onClick={handleClick}
          className={buttonStyle}
          onMouseEnter={onMouseEnter}
        >
          {!(size === 's' && pending) && children}
          {pending && (
            <div className={styles[`loader-wrapper`]}>
              <Loader outline={outline} />
            </div>
          )}
        </button>
      )}
    </div>
  );
});

export default ButtonMemo;
